import type { Plan, StripePrice } from '../types';
import type { Currency, Region } from '../types/region';
import { REGION_CONFIG } from '../types/region';

export interface ResolvedSubscriptionPlanPrice {
  price: StripePrice;
  amountMajor: number;
  currency: Currency;
  symbol: string;
}

export function normalizePriceTaxBehavior(
  taxBehavior?: string | null,
): 'inclusive' | 'exclusive' {
  return taxBehavior === 'inclusive' ? 'inclusive' : 'exclusive';
}

/**
 * Select a platform subscription Stripe price for currency + interval.
 * Prefers tax-inclusive prices when both inclusive and legacy exclusive prices exist.
 */
export function matchPlatformSubscriptionPrice<
  T extends {
    id: string;
    currency: string;
    type: string;
    recurring?: { interval: string } | null;
    tax_behavior?: string | null;
  },
>(
  prices: T[] | undefined,
  options: { currency: string; interval: 'month' | 'year' },
): T | null {
  if (!prices?.length) {
    return null;
  }

  const targetCurrency = options.currency.toUpperCase();
  const candidates = prices.filter(
    (candidate) =>
      candidate.type === 'recurring' &&
      candidate.recurring?.interval === options.interval &&
      candidate.currency?.toUpperCase() === targetCurrency,
  );

  if (!candidates.length) {
    return null;
  }

  const inclusive = candidates.find(
    (candidate) => normalizePriceTaxBehavior(candidate.tax_behavior) === 'inclusive',
  );
  return inclusive ?? candidates[0]!;
}

export function getCurrencyForRegion(region: Region | string | undefined | null): Currency {
  if (region && region in REGION_CONFIG) {
    return REGION_CONFIG[region as Region].currency;
  }
  return 'USD';
}

export function getCurrencySymbolForCurrency(currency: Currency | string): string {
  const normalized = currency.toUpperCase() as Currency;
  const match = Object.values(REGION_CONFIG).find((entry) => entry.currency === normalized);
  return match?.symbol ?? '$';
}

/**
 * Resolve a subscription plan's Stripe price for a region/currency and billing interval.
 * Never falls back to a different currency — callers must handle a missing price.
 */
export function resolveSubscriptionPlanPrice(
  plan: Pick<Plan, 'prices'>,
  options: {
    region?: Region | string | null;
    currency?: Currency | string | null;
    interval?: 'month' | 'year';
  } = {},
): ResolvedSubscriptionPlanPrice | null {
  const interval = options.interval ?? 'month';
  const targetCurrency = (
    options.currency ?? getCurrencyForRegion(options.region ?? undefined)
  ).toUpperCase() as Currency;

  const price = matchPlatformSubscriptionPrice(plan.prices, {
    currency: targetCurrency,
    interval,
  });

  if (!price) {
    return null;
  }

  return {
    price,
    amountMajor: price.unit_amount / 100,
    currency: targetCurrency,
    symbol: getCurrencySymbolForCurrency(targetCurrency),
  };
}

/** Percent off academy + club subtotal when both lines are purchased together. */
export const COMBINED_BUNDLE_DISCOUNT_PERCENT = 10;

/** Reserved platform coupon code auto-applied at combined checkout. */
export const COMBINED_BUNDLE_COUPON_CODE = 'COMBINED_BUNDLE';

export function calculateCombinedBundleSubtotal(
  academyAmountMajor: number,
  clubAmountMajor: number,
): number {
  return roundCurrencyMajor(academyAmountMajor + clubAmountMajor);
}

export function calculateCombinedBundleDiscount(subtotalMajor: number): number {
  return roundCurrencyMajor((subtotalMajor * COMBINED_BUNDLE_DISCOUNT_PERCENT) / 100);
}

export function calculateCombinedBundleTotal(subtotalMajor: number): number {
  return roundCurrencyMajor(subtotalMajor - calculateCombinedBundleDiscount(subtotalMajor));
}

function roundCurrencyMajor(amount: number): number {
  return Math.round(amount * 100) / 100;
}
